import request from '../../../../../utils/request';
import { 
  StorageContainerLabelData, 
  StorageLabelPreviewResponse, 
  StorageLabelPrintResult, 
  StorageLabelBatchPrintResult 
} from '../types/storageLabel';

/**
 * 仓储容器标签服务
 * 专为admin-staff后台仓储管理设计，与入库预报标签完全独立
 */
export class StorageLabelService {
  private readonly baseURL = '/api/v1/storage/containers/labels';
  private readonly defaultPrintAgentUrl = 'http://127.0.0.1:9135';

  /**
   * 调用标签API的通用方法
   */
  private async callLabelAPI(
    endpoint: string,
    data: any,
    options: { responseType?: 'blob' | 'json' } = {}
  ): Promise<{
    ok: boolean;
    status: number;
    statusText: string;
    headers: { get: (name: string) => string | null };
    json: () => Promise<any>;
    blob: () => Promise<any>;
    data: any;
  }> {
    try {
      console.log('[仓储标签服务] 调用API:', {
        endpoint,
        data,
        options
      });

      const requestData = {
        template_id: data.templateId,
        container_id: data.containerInfo.id,
        container_data: {
          system_code: data.containerInfo.systemCode,
          name: data.containerInfo.name,
          type: data.containerInfo.type,
          type_display: data.containerInfo.typeDisplay,
          status: data.containerInfo.status,
          status_display: data.containerInfo.statusDisplay,
          warehouse: data.containerInfo.warehouse,
          project: data.containerInfo.project,
          location: data.containerInfo.location,
          created_by: data.containerInfo.createdBy,
          created_at: data.containerInfo.createdAt,
          updated_at: data.containerInfo.updatedAt,
          remarks: data.containerInfo.remarks
        },
        content_summary: data.contentSummary,
        print_options: data.printOptions
      };

      console.log('[仓储标签服务] 发送到后端的请求数据:', requestData);

      const response = await request.post(
        `${this.baseURL}/${endpoint}/`,
        requestData,
        {
          responseType: options.responseType || 'json'
        }
      );

      // 直接返回 Axios 响应的包装对象，避免 Response 对象的问题
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        statusText: response.statusText,
        headers: {
          get: (name: string) => response.headers[name.toLowerCase()] || null
        },
        json: async () => response.data,
        blob: async () => response.data,
        data: response.data
      };

    } catch (error) {
      console.error('[仓储标签服务] API调用失败:', error);
      throw error;
    }
  }

  private getPrintAgentUrl(): string {
    try {
      const custom = String(window.localStorage.getItem('XT_PRINT_AGENT_URL') || '').trim();
      if (custom) return custom;
    } catch {}
    try {
      const env = String((process as any)?.env?.REACT_APP_PRINT_AGENT_URL || '').trim();
      if (env) return env;
    } catch {}
    return this.defaultPrintAgentUrl;
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('read blob failed'));
        reader.onload = () => {
          const raw = String(reader.result || '');
          const commaIdx = raw.indexOf(',');
          resolve(commaIdx >= 0 ? raw.slice(commaIdx + 1) : raw);
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        reject(e);
      }
    });
  }

  private async tryPrintViaLocalAgent(args: {
    blob: Blob;
    fileName: string;
    code: string;
    copies: number;
  }): Promise<{ ok: boolean; jobId?: string; error?: string }> {
    const endpoint = `${this.getPrintAgentUrl().replace(/\/$/, '')}/api/print`;
    try {
      const contentBase64 = await this.blobToBase64(args.blob);
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_type: 'STORAGE_CONTAINER_LABEL',
          label_size_in: { w: 4.0, h: 1.5 },
          copies: Math.min(50, Math.max(1, Math.floor(Number(args.copies) || 1))),
          file_name: args.fileName,
          code: args.code,
          content_base64: contentBase64,
          content_type: args.blob.type || 'application/pdf',
          hint: { source: 'xt01b-web-storage-label', ts: Date.now() }
        })
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        return { ok: false, error: `HTTP ${resp.status} ${text || ''}`.trim() };
      }
      const data: any = await resp.json().catch(() => ({}));
      return {
        ok: true,
        jobId: String(data?.job_id || data?.id || '')
      };
    } catch (e: any) {
      return { ok: false, error: e?.message || 'print agent unreachable' };
    }
  }

  /**
   * 生成标签预览
   */
  async generatePreview(data: StorageContainerLabelData): Promise<StorageLabelPreviewResponse> {
    try {
      const response = await this.callLabelAPI('preview', data, { 
        responseType: 'blob'
      });
      
      if (!response.ok) {
        throw new Error(`标签预览生成失败: ${response.statusText}`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get('content-type') || '';
      
      return {
        url: URL.createObjectURL(blob),
        isPDF: contentType.includes('application/pdf')
      };
    } catch (error) {
      console.error('生成标签预览失败:', error);
      throw error;
    }
  }

  /**
   * 下载标签PDF
   */
  async downloadLabel(data: StorageContainerLabelData): Promise<Blob> {
    try {
      console.log('[仓储标签服务] 开始下载标签:', data);
      
      const response = await this.callLabelAPI('download', data, { 
        responseType: 'blob'
      });
      
      if (!response.ok) {
        throw new Error(`标签下载失败: ${response.statusText}`);
      }

      return response.data;
    } catch (error) {
      console.error('下载标签失败:', error);
      throw error;
    }
  }

  /**
   * 直接打印标签
   */
  async printLabel(data: StorageContainerLabelData): Promise<StorageLabelPrintResult> {
    try {
      console.log('[仓储标签服务] 开始打印标签:', data);
      
      // 确保打印选项中设置了 directPrint 且 copies 为必填数字
      const normalizedCopies = Math.max(1, Math.floor(Number(data?.printOptions?.copies || 1)));
      const printData: StorageContainerLabelData = {
        ...data,
        printOptions: {
          ...data.printOptions,
          copies: normalizedCopies,
          directPrint: true
        }
      };

      // 优先走本地打印代理，保证仓库工作站真正打印到本地热敏机
      try {
        const pdfBlob = await this.downloadLabel(printData);
        const copies = normalizedCopies;
        const code = String(printData?.containerInfo?.systemCode || '').trim();
        const fileName = `storage_container_label_${code || Date.now()}.pdf`;
        const agentResult = await this.tryPrintViaLocalAgent({
          blob: pdfBlob,
          fileName,
          code,
          copies
        });
        if (agentResult.ok) {
          return {
            success: true,
            message: `标签打印已提交到本地打印代理${agentResult.jobId ? `（任务ID：${agentResult.jobId}）` : ''}`,
            printJobId: agentResult.jobId
          };
        }
        console.warn('[仓储标签服务] 本地打印代理不可用，回退后端打印接口:', agentResult.error);
      } catch (agentErr) {
        console.warn('[仓储标签服务] 代理打印尝试失败，回退后端打印接口:', agentErr);
      }

      const response = await this.callLabelAPI('print', printData);
      if (!response.ok) {
        throw new Error(`标签打印失败: ${response.statusText}`);
      }
      const result: any = await response.json();
      if (result?.print_job_id && !result?.printJobId) {
        result.printJobId = result.print_job_id;
      }
      return result as StorageLabelPrintResult;
    } catch (error) {
      console.error('打印标签失败:', error);
      throw error;
    }
  }

  /**
   * 批量打印标签
   */
  async batchPrintLabels(dataList: StorageContainerLabelData[]): Promise<StorageLabelBatchPrintResult> {
    try {
      console.log('[仓储标签服务] 开始批量打印标签:', dataList);
      
      const batchData = {
        labels: dataList.map(data => ({
          template_id: data.templateId,
          container_id: data.containerInfo.id,
          container_data: {
            system_code: data.containerInfo.systemCode,
            name: data.containerInfo.name,
            type: data.containerInfo.type,
            type_display: data.containerInfo.typeDisplay,
            status: data.containerInfo.status,
            status_display: data.containerInfo.statusDisplay,
            warehouse: data.containerInfo.warehouse,
            project: data.containerInfo.project,
            location: data.containerInfo.location,
            created_by: data.containerInfo.createdBy,
            created_at: data.containerInfo.createdAt,
            updated_at: data.containerInfo.updatedAt
          },
          content_summary: data.contentSummary,
          print_options: {
            ...data.printOptions,
            directPrint: true
          }
        }))
      };

      const response = await request.post(`${this.baseURL}/batch-print/`, batchData);
      return response.data as StorageLabelBatchPrintResult;
    } catch (error) {
      console.error('批量打印标签失败:', error);
      throw error;
    }
  }

  /**
   * 生成PDF格式标签
   */
  async generatePDF(data: StorageContainerLabelData): Promise<Blob> {
    try {
      const response = await this.callLabelAPI('generate', data, {
        responseType: 'blob'
      });
      
      if (!response.ok) {
        throw new Error(`PDF生成失败: ${response.statusText}`);
      }

      return response.data;
    } catch (error) {
      console.error('PDF生成失败:', error);
      throw error;
    }
  }

  /**
   * 获取可用的标签模板列表
   */
  async getAvailableTemplates(): Promise<Array<{ id: string; name: string; description: string }>> {
    try {
      const response = await request.get(`${this.baseURL}/templates/`);
      return response.data;
    } catch (error) {
      console.error('获取标签模板列表失败:', error);
      throw error;
    }
  }

  /**
   * 获取打印机状态
   */
  async getPrinterStatus(): Promise<{ available: boolean; name: string; status: string }> {
    try {
      const response = await request.get(`${this.baseURL}/printer-status/`);
      return response.data;
    } catch (error) {
      console.error('获取打印机状态失败:', error);
      throw error;
    }
  }
}

// 导出单例实例
export const storageLabelService = new StorageLabelService();
